import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { EmojiEvents as TrophyIcon } from '@mui/icons-material';
import { getSquareOwnerName } from '../../components/board/HitCard';
import type { WinnerGroup } from './usePublicBoardWinners';

interface WinnerCardProps {
  group: WinnerGroup;
  homeTeam: string;
  awayTeam: string;
  index: number;
}

export const WinnerCard: React.FC<WinnerCardProps> = ({
  group,
  homeTeam,
  awayTeam,
  index,
}) => {
  const { mainHit, wrongWayHit, plusNHit, plusNWrongWayHit, label, totalPayout } =
    group;
  const winnerName = getSquareOwnerName(mainHit.winningSquare);

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: 'rgba(184, 134, 11, 0.25)',
        borderLeft: '4px solid #DAA520',
        background: '#fff',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        animation: `fadeSlideIn 0.5s ease ${index * 0.1}s both`,
        '@keyframes fadeSlideIn': {
          from: { opacity: 0, transform: 'translateY(16px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '&:hover': {
          boxShadow: '0 8px 32px rgba(184, 134, 11, 0.12)',
          transform: 'translateY(-2px)',
        },
        // Subtle gold shimmer top edge
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: 'linear-gradient(90deg, transparent, #FFD700, transparent)',
          opacity: 0.5,
        },
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header: Quarter label + Payout */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrophyIcon sx={{ color: '#DAA520', fontSize: '1.2rem' }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, letterSpacing: '-0.01em', color: '#2d2d2d' }}
            >
              {label}
            </Typography>
          </Box>
          {totalPayout > 0 && (
            <Chip
              label={`$${Math.round(totalPayout)}`}
              size="small"
              sx={{
                fontWeight: 800,
                bgcolor: '#1b5e20',
                color: '#fff',
                fontSize: '0.85rem',
                letterSpacing: '0.02em',
              }}
            />
          )}
        </Box>

        {/* Score display */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            mb: 1.5,
            py: 1.2,
            px: 2,
            bgcolor: '#1a1a2e',
            borderRadius: 1.5,
            color: '#fff',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.85, fontSize: '0.8rem' }}>
            {awayTeam}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '0.08em',
              color: '#FFD700',
              fontSize: '1.1rem',
            }}
          >
            {mainHit.awayScore} &ndash; {mainHit.homeScore}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.85, fontSize: '0.8rem' }}>
            {homeTeam}
          </Typography>
        </Box>

        {/* Winner details */}
        <Table
          size="small"
          sx={{
            '& td': { border: 0, py: 0.3, px: 0.5 },
            '& td:first-of-type': { pl: 0 },
            '& td:last-of-type': { pr: 0 },
          }}
        >
          <TableBody>
            {/* Main winner */}
            <TableRow>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {winnerName}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" color="text.secondary">
                  {mainHit.payoutAmount > 0
                    ? `$${Math.round(mainHit.payoutAmount)}`
                    : ''}
                </Typography>
              </TableCell>
            </TableRow>

            {/* Wrong Way winner */}
            {wrongWayHit && (
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      label="WW"
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {getSquareOwnerName(wrongWayHit.winningSquare)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="text.secondary">
                    {wrongWayHit.payoutAmount > 0
                      ? `$${Math.round(wrongWayHit.payoutAmount)}`
                      : ''}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {/* +N winner */}
            {plusNHit && (
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      label="+N"
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {getSquareOwnerName(plusNHit.winningSquare)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="text.secondary">
                    {plusNHit.payoutAmount > 0
                      ? `$${Math.round(plusNHit.payoutAmount)}`
                      : ''}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {/* +N Wrong Way winner */}
            {plusNWrongWayHit && (
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      label="+N WW"
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {getSquareOwnerName(plusNWrongWayHit.winningSquare)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="text.secondary">
                    {plusNWrongWayHit.payoutAmount > 0
                      ? `$${Math.round(plusNWrongWayHit.payoutAmount)}`
                      : ''}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {/* Neighbor payouts */}
            {mainHit.neighborPayouts?.map((payout, idx) => (
              <TableRow key={`neighbor-${idx}`}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      label={payout.direction}
                      size="small"
                      variant="outlined"
                      color="info"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {payout.user?.displayName ||
                        payout.offlinePlayer?.displayName ||
                        'Unclaimed'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="text.secondary">
                    {payout.amount > 0 ? `$${Math.round(payout.amount)}` : ''}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
